import React, { useState } from 'react'
import { Box, Typography, Button, styled, Alert } from '@mui/material'
import { Warning, PauseCircle, DeleteForever } from '@mui/icons-material'
import DeactivateAccountModal from '../../DeactivateAccountModal'
import DeleteAccountModal from '../../DeleteAccountModal'

// ==================== STYLED COMPONENTS ====================
const SectionHeader = styled(Box)({
  marginBottom: '32px',
})

const SectionTitle = styled(Typography)({
  fontSize: '28px',
  fontWeight: 700,
  color: '#111827',
  letterSpacing: '-0.02em',
  marginBottom: '8px',
})

const SectionDescription = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#6b7280',
  lineHeight: 1.6,
})

const Card = styled(Box)({
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  marginBottom: '16px',
})

const CardHeader = styled(Box)({
  padding: '20px 24px',
  borderBottom: '1px solid #e5e7eb',
})

const CardContent = styled(Box)({
  padding: '24px',
})

const DangerCard = styled(Box)({
  padding: '20px',
  borderRadius: '10px',
  border: '1px solid #fca5a5',
  backgroundColor: '#fef2f2',
  marginBottom: '16px',

  '&:last-child': {
    marginBottom: 0,
  },
})

const IconBox = styled(Box)<{ color: string }>(({ color }) => ({
  width: 40,
  height: 40,
  borderRadius: '10px',
  backgroundColor: `${color}15`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,

  '& .MuiSvgIcon-root': {
    fontSize: '20px',
    color,
  },
}))

// ==================== COMPONENT ====================
const SecuritySection: React.FC = () => {
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const handleDeactivate = () => {
    console.log('Deactivating account...')
    // Add actual deactivation logic
  }

  const handleDelete = () => {
    console.log('Deleting account...')
    // Add actual deletion logic
  }

  return (
    <>
      <SectionHeader>
        <SectionTitle>Security & Privacy</SectionTitle>
        <SectionDescription>Manage your account security and data preferences</SectionDescription>
      </SectionHeader>

      {/* Warning Alert */}
      <Alert
        severity='warning'
        icon={<Warning />}
        sx={{
          mb: 3,
          borderRadius: '10px',
          backgroundColor: '#fffbeb',
          border: '1px solid #fde68a',
          '& .MuiAlert-icon': {
            color: '#f59e0b',
          },
          '& .MuiAlert-message': {
            fontSize: '13px',
            fontWeight: 500,
            color: '#92400e',
          },
        }}
      >
        Actions in this section are permanent and cannot be undone. Please proceed with caution.
      </Alert>

      {/* Privacy Settings */}
      <Card>
        <CardHeader>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#111827',
            }}
          >
            Privacy Settings
          </Typography>
        </CardHeader>
        <CardContent>
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#9ca3af',
              }}
            >
              Privacy settings coming soon
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card sx={{ borderColor: '#fca5a5' }}>
        <CardHeader sx={{ backgroundColor: '#fef2f2', borderBottomColor: '#fca5a5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Warning sx={{ color: '#ef4444', fontSize: 20 }} />
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#991b1b',
              }}
            >
              Danger Zone
            </Typography>
          </Box>
        </CardHeader>
        <CardContent>
          {/* Deactivate Account */}
          <DangerCard>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <IconBox color='#f97316'>
                <PauseCircle />
              </IconBox>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#111827',
                    mb: 0.5,
                  }}
                >
                  Deactivate Account
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#6b7280',
                    lineHeight: 1.6,
                    mb: 2,
                  }}
                >
                  Temporarily disable your account. You can reactivate it anytime by logging back
                  in.
                </Typography>
                <Button
                  variant='outlined'
                  size='small'
                  onClick={() => setDeactivateModalOpen(true)}
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderColor: '#fb923c',
                    color: '#f97316',
                    '&:hover': {
                      borderColor: '#f97316',
                      backgroundColor: '#fff7ed',
                    },
                  }}
                >
                  Deactivate Account
                </Button>
              </Box>
            </Box>
          </DangerCard>

          {/* Delete Account */}
          <DangerCard>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <IconBox color='#ef4444'>
                <DeleteForever />
              </IconBox>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#111827',
                    mb: 0.5,
                  }}
                >
                  Delete Account
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#6b7280',
                    lineHeight: 1.6,
                    mb: 2,
                  }}
                >
                  Permanently delete your account and all your data. This action cannot be undone.
                </Typography>
                <Button
                  variant='contained'
                  size='small'
                  onClick={() => setDeleteModalOpen(true)}
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    textTransform: 'none',
                    backgroundColor: '#ef4444',
                    '&:hover': {
                      backgroundColor: '#dc2626',
                    },
                  }}
                >
                  Delete Account
                </Button>
              </Box>
            </Box>
          </DangerCard>
        </CardContent>
      </Card>

      {/* Modals */}
      <DeactivateAccountModal
        open={deactivateModalOpen}
        onClose={() => setDeactivateModalOpen(false)}
        onConfirm={handleDeactivate}
      />
      <DeleteAccountModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
      />
    </>
  )
}

export default SecuritySection
